import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { colors, fonts, glyphs, useMediaQuery } from '../../Styles';
import { Overlay, LineInfoDisplay } from '..';
import { ButtonTypes, SelectFieldOption_Values } from '../../Definitions';
import { RootState, useAppSelector } from '../../Store';
import { Graph_QL } from '../../Services';
import { useAnimal } from '../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal';
import { MaintainAnimal } from '../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall';
import { LocaleFarmDate } from '../../Definitions/localeFarmDate';
import { UndefinedOptions, useGroupOptions, useSectionOptions } from '../_Components';
import { useDispatch, useSelector } from 'react-redux';
// import { EDIT_MODE, NOT_EDIT_MODE } from '../../Store/UI/editModeSlice';
import { ActiveEdits } from '../../Containers/FullViewScreens/GlobalScreens/Animals/animal';
import { SET_EDIT_MODE } from '../../Store/UI/editModeSlice';
import { FieldMode } from './SubComponents/lineInfoDisplay';
import NewButton, { NewButtonList } from '../BaseComponents/Buttons/newButton';
// import { useDateFormat } from '../_Components';

interface basicInfo_Props {
  onEditBasicData: () => void;
  selectedAnimal: number;
}
/**
 * BasicInfo
 * @param selectedAnimal ID for the animal to display information about.
 * @returns JSX element displaying basic info about the selected animal.
 */
export default function BasicInfo({ selectedAnimal, onEditBasicData }:basicInfo_Props) {
  const { t } = useTranslation()
  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode && state.uiState.editMode.editType === ActiveEdits.basicData);
  const groupOptions = useGroupOptions(UndefinedOptions.undefined)
  const {locationOptions} = useSectionOptions(UndefinedOptions.undefined)

  // const mobileXs = useMediaQuery(`(max-width: 450px)`);
  const dirtRef = useRef<MaintainAnimal>({animal_id: selectedAnimal});
  // const {formatDateForDisplay, formatDateForInput} = useDateFormat();

  useEffect(() => {
    dirtRef.current.animal_id = selectedAnimal;
  }, [selectedAnimal])
  
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { data: animalsState, maintainEntries: updateAnimals } = useAnimal(connection);

  const thisAnimal = useMemo(() => {
    return animalsState && animalsState[domain] ? animalsState[domain].data.find((animal) => animal.animal_id === selectedAnimal) : undefined
  },[animalsState, domain, selectedAnimal])
    
  const animalInfo = useMemo(() => {
    const animalNo = thisAnimal && thisAnimal.animal_number
    // const type = thisAnimal && thisAnimal.animalType ? typeOptions?.find(({id}) => (thisAnimal.animalType === id)) : undefined
    const lifeNo = (thisAnimal && thisAnimal.life_number) ?? undefined
    const responderNo = (thisAnimal && thisAnimal.responder_number) ?? undefined
    // const condition = thisAnimal&&thisAnimal.animalCondition?.conditionId ? conditionOptions.find(({id}) => thisAnimal.animalCondition?.conditionId === id) : undefined
    const group = thisAnimal&&thisAnimal.animalGroup.groupId ? groupOptions.find(({id}) => thisAnimal.animalGroup.groupId === id) : undefined
    const location = thisAnimal && thisAnimal.location_id ? locationOptions?.find(({id}) => (thisAnimal.location_id === id)) : undefined
    const birthDate = thisAnimal && thisAnimal.birthday

    return {
      animalNo,
      // type,
      lifeNo,
      responderNo,
      // condition,
      group,
      location,
      birthDate
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisAnimal])

  // #region | LineInfoDisplays
  const basic_animalId = <LineInfoDisplay 
    editMode={editMode}
    value={animalInfo.animalNo}
    header={t("params.animalId", {defaultValue: "Animal No."})}
    icon={"AnimalNumber"}
    fieldMode={FieldMode.input}
    onchange={(e: string|number) => dirtRef.current.animal_number = Number(e)}
    defaultValue={animalInfo.animalNo}
    isDisabled
  />

  const basic_animalType = <LineInfoDisplay 
    editMode={editMode}
    isDisabled
    value={/*type?.label*/ undefined}
    undefinedValue={t("negativeParams.animalType", {defaultValue: "No Animal Type"})}
    header={t("params.animalType", {defaultValue: "Animal Type"})}
    icon={"AnimalType"}
    fieldMode={FieldMode.input}
    // onchange={(option) => dirtRef.current.animalType = Number(option?.id)} TODO
    // options={/*TODO: typeOptions*/ []}
    // defaultValue={typeOptions?.find(({id}) => type?.id === id)} TODO
    isClearable
  />

  const basic_animalLifeNo = <LineInfoDisplay 
    editMode={editMode}
    value={animalInfo.lifeNo}
    header={t("params.lifeNo", { defaultValue: "Life No." })}
    undefinedValue={t("negativeParams.lifeNo", { defaultValue: "No Life Mo." })}
    icon={"LifeNumber"}
    fieldMode={FieldMode.input}
    onchange={(e: string|number) => dirtRef.current.life_number = e ? Number(e) : NaN}
    defaultValue={animalInfo.lifeNo ?? undefined}
    isClearable
    type='number'
  />

  const basic_animalResponderNo = <LineInfoDisplay 
    editMode={editMode}
    value={animalInfo.responderNo}
    header={t("params.responderNo", { defaultValue: "Responder No." })}
    undefinedValue={t("negativeParams.responderNo", { defaultValue: "No Responder No." })}
    icon={"ResponderNumber"} 
    fieldMode={FieldMode.input}
    onchange={(e: string) => {
      dirtRef.current.responder_number = e ? e : null
    }}
    defaultValue={animalInfo.responderNo ?? undefined}
    isClearable
    type='number'
  />

  const basic_animalCondition = <LineInfoDisplay 
    editMode={editMode}
    isDisabled
    value={/*condition?.label*/ undefined} // REMEBMER LABEL STYLE
    header={t("params.condition", { defaultValue: "Condition" })}
    undefinedValue={t("negativeParams.condition", { defaultValue: "No Condition" })}
    icon={"Condition"}
    fieldMode={FieldMode.select}
    // onchange={(option) => dirtRef.current.condition = Number(option?.id)} TODO
    options={/*TODO: conditionOptions*/ []}
    // defaultValue={conditionOptions?.find(({id}) => condition?.id === id)} TODO
    isClearable
  />

  const basic_animalGroup = <LineInfoDisplay 
    editMode={editMode}
    value={animalInfo.group?.label}
    header={t("params.group", { defaultValue: "Group" })}
    undefinedValue={t("negativeParams.group", { defaultValue: "No Group" })}
    icon={"AnimalGroup"}
    fieldMode={FieldMode.select}
    onchange={(option: SelectFieldOption_Values|undefined) => dirtRef.current.animal_group_id = option?.id ? Number(option?.id) : NaN}
    options={groupOptions}
    defaultValue={groupOptions?.find(({ id }) => animalInfo.group?.id === id) ?? undefined}
    isClearable
  />

  const basic_animalLocation = <LineInfoDisplay 
    editMode={editMode}
    value={animalInfo.location?.label}
    header={t("params.location", { defaultValue: "Location" })}
    undefinedValue={t("negativeParams.location", { defaultValue: "No Location" })}
    icon={"Location"}
    fieldMode={FieldMode.select}
    onchange={(option: SelectFieldOption_Values|undefined) => dirtRef.current.location = option?.id ? Number(option?.id) : NaN}
    options={locationOptions}
    defaultValue={locationOptions?.find(({ id }) => animalInfo.location?.id === id) ?? undefined}
    isClearable
  />

  const basic_animalBirthDate = <LineInfoDisplay 
    editMode={editMode}
    value={animalInfo.birthDate ? new LocaleFarmDate(animalInfo.birthDate).toDateForDisplay() : undefined}
    header={t("params.birthDate", { defaultValue: "Birth Date" })}
    undefinedValue={t("negativeParams.birthDate", { defaultValue: "No Birth Date" })}
    icon={"BirthDate"}
    fieldMode={FieldMode.input}
    type='date'
    onchange={(e: string|number) => dirtRef.current.birthday = (new Date(e).getTime()/1000)}
    defaultValue={animalInfo.birthDate ? new LocaleFarmDate(animalInfo.birthDate).toDateForInput(): ""}
    isClearable
  />
  // #endregion

  return (
    <div style={{boxSizing: "border-box", height: "100%"}}>
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "end", padding: "0px 20px"}}>
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, marginBottom: "5px"}}>{t("animalPages.animal.basicInfo", {defaultValue: "Basic Info"})}</p>
        { editMode
          ? <NewButtonList 
              contents={[t("buttons.save", {defaultValue: "save"}), t("buttons.cancel", {defaultValue: "cancel"}), t("buttons.edit", {defaultValue: "edit"})]} 
              onClick={(index: number) => {
                if (index === 0) { dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined })); if (updateAnimals) {updateAnimals({data: [dirtRef.current]});}; dirtRef.current = {animal_id: selectedAnimal}} 
                if (index === 1) { dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined })); dirtRef.current = {animal_id: selectedAnimal}}
                if (index === 2) {}
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Edit({})]}
              disabled={[false, false, true]}
              overrides={(index: number) => ({
                baseStyles: {
                  backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary
                }
              })}
            />
          : <NewButton content={t("buttons.edit", { defaultValue: "edit" })} startEnhancer={glyphs.Edit({})} onClick={() => onEditBasicData()} buttonType={ButtonTypes.tableButton}/>
        }
      </div>

      <div style={{overflowY: "scroll", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.greySecondary+colors.transparent, backgroundColor: colors.greyPrimary, border: "5px solid "+colors.greyPrimary, padding: "20px", width: "100%", height: "calc(100% - 50px)", borderRadius: "10px", boxSizing: "border-box"}}>
        { !thisAnimal
          ? <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyLightTertiary}}>{t("animalPages.animal.noAnimalSelected", {defaultValue: "No animal selected"})}</div>
          : <div style={{display: "flex", flexWrap: "wrap", margin: "0 auto", gap: "10px"}}>
              {basic_animalId}{basic_animalType}
              {basic_animalLifeNo}{basic_animalResponderNo}
              {basic_animalCondition}{basic_animalGroup}
              {basic_animalLocation}{basic_animalBirthDate}
            </div>
        }
      </div>
    </div>
  );
}